import { Injectable, signal } from '@angular/core';

import {
  AppNotification,
  NotificationOptions,
} from '../../shared/components/notifications/notification.model';

/** Tiempo que se queda una notificación en pantalla si no se dice otra cosa. */
const DEFAULT_DURATION_MS = 6000;

/**
 * Lo que dura la animación de salida. Debe ir en paralelo con la de
 * `notifications.component.scss`: al cerrar, la notificación se queda en el DOM
 * marcada como `leaving` durante este tiempo y luego se retira.
 */
export const NOTIFICATION_EXIT_MS = 280;

/**
 * Avisos flotantes en la esquina superior derecha. El contenedor
 * (`app-notifications`) se monta una sola vez en la raíz de la app, así que
 * desde cualquier componente basta con inyectar este servicio y llamar a
 * `success()` / `info()` / `error()`.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly items = signal<readonly AppNotification[]>([]);
  /** Temporizadores del cierre automático. */
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();
  /** Temporizadores de la animación de salida de las que ya se están cerrando. */
  private readonly exitTimers = new Map<number, ReturnType<typeof setTimeout>>();
  private lastId = 0;

  /** Notificaciones vivas, de la más antigua a la más reciente. */
  readonly notifications = this.items.asReadonly();

  show(title: string, message?: string, options: NotificationOptions = {}): number {
    const id = ++this.lastId;

    this.items.update((current) => [
      ...current,
      { id, title, message, variant: options.variant ?? 'success' },
    ]);

    const duration = options.durationMs ?? DEFAULT_DURATION_MS;
    if (duration > 0) {
      this.timers.set(
        id,
        setTimeout(() => this.dismiss(id), duration),
      );
    }

    return id;
  }

  success(title: string, message?: string, options: NotificationOptions = {}): number {
    return this.show(title, message, { ...options, variant: 'success' });
  }

  info(title: string, message?: string, options: NotificationOptions = {}): number {
    return this.show(title, message, { ...options, variant: 'info' });
  }

  error(title: string, message?: string, options: NotificationOptions = {}): number {
    return this.show(title, message, { ...options, variant: 'error' });
  }

  /**
   * Reprograma el cierre de una notificación que ya está en pantalla. Sirve para
   * las que se muestran con `durationMs: 0` mientras dura una tarea: al acabar
   * ésta se les da el tiempo que deben quedarse visibles.
   */
  dismissAfter(id: number, durationMs: number): void {
    const pending = this.items().some(
      (notification) => notification.id === id && !notification.leaving,
    );
    if (!pending) return;

    const timer = this.timers.get(id);
    if (timer !== undefined) clearTimeout(timer);

    this.timers.set(
      id,
      setTimeout(() => this.dismiss(id), durationMs),
    );
  }

  /**
   * Arranca la salida: la marca como `leaving` para que corra la animación y la
   * retira del DOM al terminar ésta.
   */
  dismiss(id: number): void {
    const timer = this.timers.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      this.timers.delete(id);
    }

    const pending = this.items().some(
      (notification) => notification.id === id && !notification.leaving,
    );
    if (!pending) return;

    this.items.update((current) =>
      current.map((notification) =>
        notification.id === id ? { ...notification, leaving: true } : notification,
      ),
    );

    this.exitTimers.set(
      id,
      setTimeout(() => this.remove(id), NOTIFICATION_EXIT_MS),
    );
  }

  dismissAll(): void {
    this.timers.forEach((timer) => clearTimeout(timer));
    this.timers.clear();
    this.exitTimers.forEach((timer) => clearTimeout(timer));
    this.exitTimers.clear();
    this.items.set([]);
  }

  /** Retirada real del DOM, ya terminada la animación. */
  private remove(id: number): void {
    this.exitTimers.delete(id);
    this.items.update((current) => current.filter((notification) => notification.id !== id));
  }
}
